import { IconButton, Stack, Text, TextInput, tokens } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';
import styled from '@emotion/styled';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePlanForFeature } from '@hotjar/webapp/hooks/usePlanForFeature';
import { useCurrentSite } from '@hotjar/site';
import type { Site } from '@hotjar/site';
import type { TargetingRule } from '@hotjar/feedback/domain/common/TargetingRule/TargetingRule';
import { validateRuleAndNormalize } from './validators';
import { AdvancedSelect } from '../../AdvancedSelectA11y/AdvancedSelect';
import { IconOption } from '../../AdvancedSelectA11y/IconOption';
import {
  MATCH_OPERATION,
  RULE_TRIGGER_TYPE,
} from '../../../../domain/common/TargetingRule/TargetingRule';
import { useAskFeature } from '../../../../domain/common/Feature/useAskFeature';

const inputWidth = '400px';

const StyledText = styled(Text)`
  margin-top: ${tokens.spacer.x0};
  display: block;
  max-width: ${inputWidth};
`;

const StyledInput = styled(TextInput)`
  width: ${inputWidth};
`;

const StyledIconButton = styled(IconButton)<{ canRemove: boolean }>`
  margin-left: ${tokens.spacer.x0};
  ${({ canRemove }) => !canRemove && `visibility: hidden;`};
`;

interface OperationDetails {
  id: string;
  labelKey: string;
  placeholder: string;
  operation: MATCH_OPERATION;
  triggerType: RULE_TRIGGER_TYPE;
}

export const operationOptions: OperationDetails[] = [
  {
    id: '1',
    triggerType: RULE_TRIGGER_TYPE.URL,
    operation: MATCH_OPERATION.SIMPLE,
    labelKey: 'simple-url-match',
    placeholder: 'https://www.example.com/',
  },
  {
    id: '2',
    triggerType: RULE_TRIGGER_TYPE.URL,
    operation: MATCH_OPERATION.EXACT,
    labelKey: 'exact-url-match',
    placeholder: 'https://www.example.com/?product=X',
  },
  {
    id: '3',
    triggerType: RULE_TRIGGER_TYPE.URL,
    operation: MATCH_OPERATION.STARTS_WITH,
    labelKey: 'url-starts-with',
    placeholder: 'https://www.example.com/page/',
  },
  {
    id: '4',
    triggerType: RULE_TRIGGER_TYPE.URL,
    operation: MATCH_OPERATION.ENDS_WITH,
    labelKey: 'url-ends-with',
    placeholder: '/detail',
  },
  {
    id: '5',
    triggerType: RULE_TRIGGER_TYPE.URL,
    operation: MATCH_OPERATION.CONTAINS,
    labelKey: 'url-contains',
    placeholder: 'register/',
  },
  {
    id: '6',
    triggerType: RULE_TRIGGER_TYPE.URL,
    operation: MATCH_OPERATION.REGEX,
    labelKey: 'url-regex',
    placeholder: '\\/pages\\/[^\\/]+',
  },
  {
    id: '7',
    triggerType: RULE_TRIGGER_TYPE.TRIGGER,
    operation: MATCH_OPERATION.EXACT,
    labelKey: 'event',
    placeholder: 'saw_homepage_variation_b',
  },
];

const operationsWithoutTriggers: OperationDetails[] = operationOptions.filter(
  (operation) => operation.triggerType !== RULE_TRIGGER_TYPE.TRIGGER,
);

const FEATURE_NAME = 'targeting.triggers';

function getSelectedOperationDetails(
  triggerType: RULE_TRIGGER_TYPE,
  operation: MATCH_OPERATION | undefined,
): OperationDetails | undefined {
  if (!operation) {
    return;
  }
  return operationOptions.find(
    (matchOperation) =>
      matchOperation.triggerType === triggerType && matchOperation.operation === operation,
  );
}

export interface PageTargetingRuleProps {
  canRemove: boolean;
  onRemove: () => void;
  onUpsell: () => void;
  includeEvents?: boolean;
  rule: TargetingRule;
  autoValidate: boolean;
}

export function PageTargetingRule({
  onRemove,
  canRemove,
  includeEvents,
  rule,
  onUpsell,
  autoValidate,
}: PageTargetingRuleProps): JSX.Element {
  const { t } = useTranslation('feedback', { keyPrefix: 'wizard.section.targeting.page' });
  const { t: commonT } = useTranslation('common');
  const [pattern, setPattern] = useState(rule.pattern);
  const [operation, setOperation] = useState(rule.match_operation);
  const [triggerType, setTriggerType] = useState(rule.component);
  const [error, setError] = useState<string>();
  const { data: site = {} as Site } = useCurrentSite();
  const canUseTriggers = useAskFeature(FEATURE_NAME, site);
  const planNeededForTriggers = usePlanForFeature(FEATURE_NAME);
  const getTranslatedRule = useCallback(
    (ruleTrigger, ruleOperation) => {
      const operationLabelKey = getSelectedOperationDetails(ruleTrigger, ruleOperation)?.labelKey;
      return operationLabelKey ? t(operationLabelKey) : '';
    },
    [t],
  );

  const validate = useCallback(() => {
    validateRuleAndNormalize(rule);
    setPattern(rule.pattern);
    setError(rule.error);
  }, [rule, setError]);

  useEffect(() => {
    if (autoValidate) {
      validate();
    }
  }, [validate, autoValidate]);

  const selectedValue = useMemo(() => {
    return (
      operationOptions.find((o) => o.operation === operation && o.triggerType === triggerType)
        ?.id ?? (includeEvents === true ? operationOptions : operationsWithoutTriggers)[0].id
    );
  }, [operation, triggerType, includeEvents]);

  return (
    <Stack direction="row" spacing="x1">
      <AdvancedSelect
        id="page-targeting-rule-selector"
        selectedValue={selectedValue}
        onSelect={(id: string) => {
          const matchOperation = operationOptions.find((o) => o.id === id);
          if (matchOperation === undefined) return;

          if (matchOperation.triggerType === RULE_TRIGGER_TYPE.TRIGGER && !canUseTriggers) {
            onUpsell();
            return;
          }
          if (operation !== matchOperation.operation) {
            setOperation(matchOperation.operation);
            // eslint-disable-next-line camelcase
            rule.match_operation = matchOperation.operation;
          }
          if (triggerType !== matchOperation.triggerType) {
            setTriggerType(matchOperation.triggerType);
            rule.component = matchOperation.triggerType;
          }
          validate();
        }}
        ariaLabel={`${rule.negate ? t('negate-operation') : t('operation')}: ${getTranslatedRule(
          triggerType,
          operation,
        )}`}
        maxWidth={220}
      >
        {(includeEvents === true ? operationOptions : operationsWithoutTriggers).map((o) => (
          <IconOption
            id={o.id}
            key={o.id}
            value={o.id}
            planBadge={
              o.triggerType === RULE_TRIGGER_TYPE.TRIGGER &&
              !canUseTriggers &&
              planNeededForTriggers
                ? planNeededForTriggers
                : undefined
            }
          >
            {t(o.labelKey)}
          </IconOption>
        ))}
      </AdvancedSelect>
      <div>
        <StyledInput
          value={pattern as string}
          placeholder={`${commonT('for-example')} ${
            getSelectedOperationDetails(triggerType, operation)?.placeholder
          }`}
          onChange={(e) => {
            const query = e.target.value;
            setPattern(query);
            rule.pattern = query;
            if (
              operation === MATCH_OPERATION.SIMPLE &&
              (query.includes('#') || query.includes('?'))
            ) {
              setOperation(MATCH_OPERATION.EXACT);
              // eslint-disable-next-line camelcase
              rule.match_operation = MATCH_OPERATION.EXACT;
            }
            if (rule.error !== undefined) {
              validate();
            }
          }}
          isInvalid={error !== undefined}
          onBlur={() => validate()}
        />
        {error !== undefined && (
          <StyledText textColor="text_error">{t(`error.${error}`)}</StyledText>
        )}
      </div>

      <StyledIconButton
        kind="ghost"
        icon="trash"
        onClick={onRemove}
        tooltipText={t('remove-rule')}
        canRemove={canRemove}
        description={t('remove-rule')}
      />
    </Stack>
  );
}
